import axios from 'axios';
import { getAccessToken, clearAuthStorage } from '../utils/authHelpers';
import { invalidateCachesFor } from '../utils/cacheInvalidation';
import { PASSWORD_SETUP_CODE, setPasswordSetup } from '../utils/passwordSetup';
import { attachCriticalConfirm, retryAfterCriticalRefusal } from '../utils/criticalChange';
import { applyTrashView } from '../utils/trashView';
import { isPublicPath } from '../utils/publicPaths';

// API Base URL 설정 (nginx 프록시를 통해 백엔드로 전달)
const BASE_URL = '';

// axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10초 타임아웃
});

// 요청 인터셉터: 토큰이 있으면 자동으로 헤더에 추가
apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    /* 휴지통 캠페인 읽기 전용 전체 보기(2026-10-23) — 읽기엔 머리글, 쓰기는 여기서 멈춘다(확인 창도 안 띄운다) */
    try {
      applyTrashView(config);
    } catch (e) {
      return Promise.reject(e);
    }

    // 핵심 값(단가·마진·정산·배송비) 변경이면 본인 확인을 받고 확인 표시를 붙인다. 취소하면 요청을 보내지 않는다.
    return attachCriticalConfirm(config);
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 에러 처리
apiClient.interceptors.response.use(
  (response) => {
    /* 저장이 끝났으면 그 값을 담고 있던 보관함을 비운다 — 안 비우면 방금 적은 값을
       화면이 최대 3분간 못 본다(2026-08-12 「마감일 적었는데 왜 안 되지」).
       실패한 요청에서는 부르지 않는다 — 서버가 안 받았으면 옛 값이 아직 맞는 값이다. */
    invalidateCachesFor(response.config?.url, response.config?.method);
    return response;
  },
  async (error) => {
    /* 서버가 「확인 없는 핵심 값 변경」이라며 거부(428)하면 확인을 받아 한 번만 다시 보낸다 */
    const retried = await retryAfterCriticalRefusal(error, apiClient);
    if (retried) return retried;

    /* 인증 관련 엔드포인트에서는 401을 정상 응답으로 처리.
       비밀번호 바꾸기도 여기에 넣는다 — 「지금 비밀번호」를 틀리게 적은 것은 «적어 넣은 값»이
       틀린 것이지 로그인이 끊긴 것이 아니다. 빼 두면 오타 한 번에 토큰이 지워져 로그인 화면으로
       쫓겨난다(2026-08-13 실제로 겪음). 서버도 400으로 돌려주지만, 여기서도 막아 둔다. */
    const url = error.config?.url || '';
    const isAuthEndpoint = url.includes('/api/auth/') || url.includes('/api/user/password');

    // API 에러 응답 로그 출력 (에러만)
    if (error.response) {
      console.error(`[API Response Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
        status: error.response.status,
        data: error.response.data
      });
    } else {
      console.error('[API Response Error]', error.message);
    }

    // 서버가 «비밀번호부터 정하라»며 막으면(옛 세션·다른 로그인 경로 포함) 온보딩 창을 띄운다
    if (error.response?.status === 403 && error.response?.data?.error?.code === PASSWORD_SETUP_CODE) {
      setPasswordSetup(true);
    }

    // 401 에러 시 로그인 페이지로 리다이렉트 (단, 인증 엔드포인트 제외)
    if (error.response?.status === 401 && !isAuthEndpoint && !isPublicPath(window.location.pathname)) {
      // serviceType은 클리어 전에 읽어둠
      const serviceType = localStorage.getItem('serviceType');
      const target = serviceType === 'INFLUENCER' ? '/login' : '/';

      // 인증 관련 키 정리
      clearAuthStorage();

      // 이미 진입점에 있는 경우 리다이렉트 루프 방지
      const currentPath = window.location.pathname;
      if (currentPath !== target && currentPath !== '/login' && currentPath !== '/') {
        window.location.href = target;
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
